import { useEffect, useState, useRef } from 'react'
import { useMutation } from '@apollo/client'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useUser } from '../../../../common/context/User'
import { UPDATE_SUBSCRIPTION_PLAN_TO_FREE } from '../../../../common/graphql/billing'
import { MODALS, PlanInterval } from '../../../../common/types'
import { QUERY_ACCOUNT } from '../../../../common/graphql/account'
import { ORGANIZATION_ACTIONS } from '../../../../common/events/orgEvents'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'

import { AlertDialog, Button, Flex, Text, toast } from '@buffer-mono/popcorn'
import capitalize from 'lodash/capitalize'

const ConfirmCancellationModal = (): JSX.Element => {
  const account = useUser()
  const {
    openModal,
    modalData: data,
    dismissModal: closeAction,
  } = useModalManager()

  const subscriptionType = data?.subscriptionType || 'plan'
  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(true)
  const isLoadingRef = useRef(false)

  const commonTrackingProperties =
    account?.currentOrganization?.commonTrackingProperties || null
  const planName =
    account?.currentOrganization?.billing?.subscription?.plan?.name || ''
  const renewalDate =
    account?.currentOrganization?.billing?.subscription?.periodEnd || ''
  const organizationId = account?.currentOrganization?.id

  const dateOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  } as Intl.DateTimeFormatOptions
  const formattedRenewalDate = new Date(renewalDate).toLocaleDateString(
    'en-US',
    dateOptions,
  )

  const { currentOrganization } = account
  const connectedChannelsCount =
    currentOrganization && currentOrganization.channels
      ? account?.channels?.filter(
          (channel) =>
            channel.organizationId === account?.currentOrganization?.id &&
            !channel.isLocked,
        ).length || 0
      : 0

  const onOpenChange = (open: boolean): void => {
    if (open) return
    if (isLoadingRef.current) {
      return
    }
    setOpen(false)
    closeAction()
  }

  const errorMessage = `Whoops! Looks like there was a problem cancelling your ${subscriptionType}. Please try again. If you continue experiencing this problem, please contact us.`

  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION_PLAN_TO_FREE, {
    refetchQueries: [{ query: QUERY_ACCOUNT }],
    awaitRefetchQueries: true,
  })

  const handleConfirmCancellation = async (): Promise<void> => {
    isLoadingRef.current = true
    setIsLoading(true)

    try {
      const { data: result } = await updateSubscription({
        variables: {
          interval: PlanInterval.month,
          organizationId: account?.currentOrganization?.id,
          plan: 'free',
        },
      })

      if (result?.billingUpdateSubscriptionPlan.userFriendlyMessage) {
        toast.error(result.billingUpdateSubscriptionPlan.userFriendlyMessage)
      } else {
        ORGANIZATION_ACTIONS.billingUpdated({ account })
        setOpen(false)
        closeAction()
        openModal({
          key: MODALS.success,
          data: {
            selectedPlan: { planName: 'free' },
            isDowngradingToFreePlan: true,
          },
        })
      }
    } catch (error) {
      toast.error(errorMessage)
    } finally {
      isLoadingRef.current = false
      setIsLoading(false)
    }
  }

  const handleCancel = (): void => {
    if (isLoadingRef.current) return
    organizationId &&
      BufferTracker.confirmCancellationCTAClicked({
        organizationId,
        ctaName: 'doNotCancel_cancelConfirm',
        ...commonTrackingProperties,
      })
    setOpen(false)
    closeAction()
  }

  const channelsCopy = (
    <Flex direction="column" gap="xs">
      <Text weight="bold">What happens on the Free plan?</Text>
      <Text>
        You currently have {connectedChannelsCount} channels connected to your
        Buffer account.{' '}
        <strong>
          On your downgrade date, all but 3 of these channels will be locked and
          you will not be able to post to them.
        </strong>{' '}
        You can choose which channels are unlocked by visiting the
        &ldquo;Channels&rdquo; page within your Account.
      </Text>
    </Flex>
  )

  const planCopy = (
    <Flex
      direction="column"
      gap="lg"
      data-testid="confirm-cancellation-modal-message"
    >
      <Text>
        You&apos;ve already paid for the <strong>{planName}</strong> plan until{' '}
        <strong>{formattedRenewalDate}</strong>. If you cancel now, you&apos;ll
        keep full access until then. After that, your account will automatically
        switch to the Free plan.
      </Text>
      {connectedChannelsCount > 3 && channelsCopy}
    </Flex>
  )

  const trialCopy = (
    <Text data-testid="confirm-cancellation-modal-message">
      Please confirm you&apos;d like to cancel your trial.
    </Text>
  )

  useEffect(() => {
    if (organizationId) {
      BufferTracker.confirmCancellationViewed({
        organizationId,
        ...commonTrackingProperties,
      })
    }
  }, [organizationId, commonTrackingProperties])

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Content>
        <AlertDialog.Title>
          {`Confirm ${capitalize(subscriptionType)} Cancellation`}
        </AlertDialog.Title>
        <AlertDialog.Description>
          {subscriptionType === 'plan' ? planCopy : trialCopy}
        </AlertDialog.Description>
        <AlertDialog.Separator />
        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button
              variant="secondary"
              disabled={isLoading}
              onClick={handleCancel}
            >
              Do Not Cancel
            </Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              variant="critical"
              disabled={isLoading}
              loading={isLoading}
              onClick={handleConfirmCancellation}
            >
              {isLoading
                ? 'Cancelling...'
                : `Cancel My ${capitalize(subscriptionType)}`}
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}

export default ConfirmCancellationModal
